import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
  ArrowRightLeft,
  Bot,
  User,
  GripVertical,
  Circle,
  Building2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useActiveCall } from "@/contexts/ActiveCallContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useSocket } from "@/contexts/SocketContext";
import {
  useInboxUsers,
  useInboxDepartments,
} from "@/pages/internal/inbox/hooks/useInboxData";

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Floating call bar — visible globally while a call is active.
 * Positioned fixed, draggable by the user. Defaults to bottom-right.
 * Based on Dribbble reference: dark card with avatar, name, timer, action buttons.
 */
export function ActiveCallBar() {
  const {
    activeCall,
    elapsed,
    endCall,
    acceptCall,
    rejectCall,
    acceptTransfer,
    rejectTransfer,
    clearTransferRejection,
    toggleMute,
    handoff,
    hasActiveCall,
  } = useActiveCall();
  const { user: authUser } = useAuth();
  const { on: onSocketEvent } = useSocket();

  // Drag state — use transform for GPU-accelerated movement
  const [position, setPosition] = useState(null); // null = default bottom-right via CSS
  const dragRef = useRef(null);
  const isDraggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  // Transfer dropdown state
  const { users: baseUsers } = useInboxUsers();
  const { data: baseDepartments = [] } = useInboxDepartments();
  const [presenceOverrides, setPresenceOverrides] = useState({});
  const users = useMemo(
    () =>
      (baseUsers || []).map((u) => ({
        ...u,
        ...(presenceOverrides[u.id] || {}),
      })),
    [baseUsers, presenceOverrides],
  );
  const departments = baseDepartments;
  const [transferSearch, setTransferSearch] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [showRejectNote, setShowRejectNote] = useState(false);

  // Listen for presence updates
  useEffect(() => {
    if (!onSocketEvent) return;
    const unsub = onSocketEvent("presence:update", (data) => {
      setPresenceOverrides((prev) => ({
        ...prev,
        [data.userId]: {
          isOnline: data.isOnline,
          presenceStatus: data.presenceStatus,
        },
      }));
    });
    return unsub;
  }, [onSocketEvent]);

  // Filter users for transfer (exclude current user)
  const filteredTransferUsers = useMemo(() => {
    let list = users.filter(
      (u) => u.id !== authUser?.id && u.id !== authUser?.userId,
    );
    if (transferSearch) {
      const q = transferSearch.toLowerCase();
      list = list.filter(
        (u) =>
          u.fullName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [users, transferSearch, authUser?.id, authUser?.userId]);

  // Filter departments for transfer
  const filteredDepartments = useMemo(() => {
    if (!transferSearch) return departments;
    const q = transferSearch.toLowerCase();
    return departments.filter((d) => d.name?.toLowerCase().includes(q));
  }, [departments, transferSearch]);

  // Handle transfer to agent — V2 (desabilitado na V1)
  const handleTransfer = useCallback((agentId) => {
    toast.info("Transferência para agente em desenvolvimento");
  }, []);

  // Return ticket to queue — V2 (desabilitado na V1)
  const handleReturnToQueue = useCallback(() => {
    toast.info("Voltar para fila em desenvolvimento");
  }, []);

  // Transfer ticket to department — V2 (desabilitado na V1)
  const handleTransferToDepartment = useCallback((departmentId) => {
    toast.info("Transferência para departamento em desenvolvimento");
  }, []);

  // Auto-clear transfer rejection notification after 5s
  useEffect(() => {
    if (!activeCall?.transferRejection) return;
    const timer = setTimeout(() => clearTransferRejection(), 5000);
    return () => clearTimeout(timer);
  }, [activeCall?.transferRejection, clearTransferRejection]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!dragRef.current) return;
      isDraggingRef.current = true;
      const rect = dragRef.current.getBoundingClientRect();
      offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      // If first drag, capture current position from CSS layout
      if (!position) {
        setPosition({ x: rect.left, y: rect.top });
      }
      // Apply willChange during drag for GPU compositing
      dragRef.current.style.willChange = "transform";
      document.body.style.userSelect = "none";
      e.preventDefault();
    },
    [position],
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !dragRef.current) return;
      // Use rAF to batch updates — avoids layout thrashing
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (!dragRef.current) return;
        const barW = dragRef.current.offsetWidth;
        const barH = dragRef.current.offsetHeight;
        const x = Math.max(
          0,
          Math.min(window.innerWidth - barW, e.clientX - offsetRef.current.x),
        );
        const y = Math.max(
          0,
          Math.min(window.innerHeight - barH, e.clientY - offsetRef.current.y),
        );
        // Apply transform directly to DOM — no React re-render during drag
        dragRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        // Store for React state sync on mouseUp
        offsetRef.current._lastX = x;
        offsetRef.current._lastY = y;
      });
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.userSelect = "";
        if (dragRef.current) dragRef.current.style.willChange = "auto";
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        // Sync final position to React state (single re-render)
        if (offsetRef.current._lastX != null) {
          setPosition({
            x: offsetRef.current._lastX,
            y: offsetRef.current._lastY,
          });
          delete offsetRef.current._lastX;
          delete offsetRef.current._lastY;
        }
      }
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (!hasActiveCall) return null;

  const isRinging = activeCall.status === "RINGING";
  const isActive = ["ACCEPTED", "IN_PROGRESS"].includes(activeCall.status);
  const isIncoming = activeCall.direction === "USER_INITIATED";
  const isAiHandled = activeCall.handlerType === "AI_AGENT";
  const isTransferRinging = isRinging && activeCall.isTransfer;
  const hasPendingTransfer = !!activeCall.pendingTransfer;
  const isReconnecting = activeCall.audioState === "reconnecting";
  const isAudioFailed = activeCall.audioState === "failed";

  // Status label
  const statusLabel = isReconnecting
    ? "Reconectando áudio..."
    : isAudioFailed
      ? "Áudio perdido"
      : isTransferRinging
        ? `Transferência de ${activeCall.transferFrom?.agentName || "agente"}...`
        : hasPendingTransfer
          ? `Transferindo para ${activeCall.pendingTransfer.toAgentName}...`
          : isRinging
            ? isIncoming
              ? "Chamada recebida..."
              : "Chamando..."
            : activeCall.aiSpeaking
              ? "IA falando..."
              : formatTime(elapsed);

  // Positioning style: CSS transform for GPU-accelerated compositing
  const positionStyle = position
    ? {
        position: "fixed",
        left: 0,
        top: 0,
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      }
    : { bottom: 24, right: 24 };

  return (
    <div
      ref={dragRef}
      className="fixed z-[9999] animate-in slide-in-from-bottom-4 fade-in duration-300"
      style={positionStyle}
    >
      <div className="bg-surface border border-border rounded-2xl shadow-2xl shadow-black/50 p-4 min-w-[320px] max-w-[380px]">
        {/* Top: Drag handle + Avatar + Contact info */}
        <div
          onMouseDown={handleMouseDown}
          className="flex items-center gap-3 mb-3 cursor-grab active:cursor-grabbing select-none"
        >
          {/* Drag handle */}
          <div
            className="p-0.5 -ml-1 text-foreground-muted hover:text-foreground-muted transition-colors"
            title="Arrastar"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="relative">
            <Avatar
              className={`h-10 w-10 border-2 ${isTransferRinging ? "border-blue-500" : isRinging ? "border-amber-500" : hasPendingTransfer ? "border-blue-500" : "border-emerald-500"}`}
            >
              <AvatarImage
                src={activeCall.contactAvatar}
                alt={activeCall.contactName}
              />
              <AvatarFallback className="bg-surface-active text-foreground-secondary text-sm">
                {getInitials(activeCall.contactName)}
              </AvatarFallback>
            </Avatar>
            {isRinging && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-500 animate-pulse" />
            )}
            {isActive && isAiHandled && (
              <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-violet-600 flex items-center justify-center">
                <Bot className="h-2.5 w-2.5 text-foreground-strong" />
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground-strong truncate">
              {activeCall.contactName}
            </p>
            <div className="flex items-center gap-1.5">
              {isIncoming && isRinging && !isTransferRinging && (
                <PhoneIncoming className="h-3 w-3 text-amber-700 dark:text-amber-400" />
              )}
              {isTransferRinging && (
                <ArrowRightLeft className="h-3 w-3 text-blue-700 dark:text-blue-400" />
              )}
              <p
                className={`text-xs font-mono ${isTransferRinging ? "text-blue-700 dark:text-blue-400" : hasPendingTransfer ? "text-blue-700 dark:text-blue-400" : isRinging ? "text-amber-700 dark:text-amber-400" : "text-foreground-muted"}`}
              >
                {statusLabel}
              </p>
            </div>
          </div>
          {isAiHandled && isActive && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-200 dark:bg-violet-900/60 text-violet-700 dark:text-violet-300 font-medium">
              IA
            </span>
          )}
        </div>

        {/* Transcript preview — last AI or user speech */}
        {activeCall.transcripts?.length > 0 && isActive && (
          <div className="mb-3 px-2 py-1.5 bg-surface-active/50 rounded-lg max-h-16 overflow-y-auto custom-scrollbar">
            <p className="text-xs text-foreground-muted truncate">
              <span
                className={
                  activeCall.transcripts.at(-1).speaker === "USER"
                    ? "text-blue-700 dark:text-blue-400"
                    : "text-violet-700 dark:text-violet-400"
                }
              >
                {activeCall.transcripts.at(-1).speaker === "USER"
                  ? "Contato:"
                  : "IA:"}
              </span>{" "}
              <span className="text-foreground-secondary">
                {activeCall.transcripts.at(-1).text}
              </span>
            </p>
          </div>
        )}

        {/* Transfer rejection notification */}
        {activeCall.transferRejection && (
          <div className="mb-3 px-3 py-2 bg-red-900/30 border border-red-800/50 rounded-lg">
            <p className="text-xs text-red-300">
              <span className="font-semibold">
                {activeCall.transferRejection.toAgentName}
              </span>{" "}
              recusou a transferência
              {activeCall.transferRejection.reason && (
                <span className="text-red-700 dark:text-red-400 block mt-0.5">
                  "{activeCall.transferRejection.reason}"
                </span>
              )}
            </p>
          </div>
        )}

        {/* Bottom: Action buttons */}
        <div className="flex items-center justify-between gap-2">
          <TooltipProvider delayDuration={200}>
            {/* INCOMING TRANSFER: Accept + Reject with optional note */}
            {isTransferRinging ? (
              <>
                {showRejectNote ? (
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      placeholder="Motivo (opcional)..."
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      className="h-8 bg-surface-active border-border-strong text-foreground text-xs flex-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          rejectTransfer(rejectNote);
                          setRejectNote("");
                          setShowRejectNote(false);
                        }
                        if (e.key === "Escape") {
                          setShowRejectNote(false);
                          setRejectNote("");
                        }
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        rejectTransfer(rejectNote);
                        setRejectNote("");
                        setShowRejectNote(false);
                      }}
                      className="h-8 px-2 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md"
                    >
                      Enviar
                    </Button>
                  </div>
                ) : (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowRejectNote(true)}
                          className="h-11 w-11 rounded-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          <PhoneOff className="h-5 w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="bg-surface-active text-foreground border-border-strong"
                      >
                        Recusar transferência
                      </TooltipContent>
                    </Tooltip>

                    <div className="flex-1 text-center">
                      <span className="text-[10px] text-blue-700 dark:text-blue-400">
                        Transferência de{" "}
                        {activeCall.transferFrom?.agentName || "agente"}
                      </span>
                    </div>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={acceptTransfer}
                          className="h-11 w-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Phone className="h-5 w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="bg-surface-active text-foreground border-border-strong"
                      >
                        Aceitar transferência
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </>
            ) : isRinging && isIncoming ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={rejectCall}
                      className="h-11 w-11 rounded-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      <PhoneOff className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-surface-active text-foreground border-border-strong"
                  >
                    Rejeitar chamada
                  </TooltipContent>
                </Tooltip>

                <div className="flex-1" />

                {isAiHandled && (
                  <span className="text-[10px] text-foreground-muted px-2">
                    IA atendendo...
                  </span>
                )}

                <div className="flex-1" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={acceptCall}
                      className="h-11 w-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Phone className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-surface-active text-foreground border-border-strong"
                  >
                    Aceitar chamada
                  </TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                {/* ACTIVE: Mute + Handoff + End */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!isActive}
                      onClick={toggleMute}
                      className={`h-10 w-10 rounded-full ${
                        activeCall.muted
                          ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/60"
                          : "bg-surface-active text-foreground-muted hover:bg-border-strong hover:text-foreground"
                      }`}
                    >
                      {activeCall.muted ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-surface-active text-foreground border-border-strong"
                  >
                    {activeCall.muted ? "Ativar microfone" : "Silenciar"}
                  </TooltipContent>
                </Tooltip>

                {/* Transfer dropdown */}
                <DropdownMenu onOpenChange={() => setTransferSearch("")}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={
                            !isActive ||
                            !activeCall.ticketId ||
                            hasPendingTransfer
                          }
                          className={`h-10 w-10 rounded-full ${hasPendingTransfer ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400" : "bg-surface-active text-foreground-muted hover:bg-border-strong hover:text-foreground"}`}
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="bg-surface-active text-foreground border-border-strong"
                    >
                      Transferir para agente
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent
                    align="center"
                    side="top"
                    className="bg-surface border-border w-56 p-1"
                  >
                    <Input
                      placeholder="Buscar agente ou departamento..."
                      value={transferSearch}
                      onChange={(e) => setTransferSearch(e.target.value)}
                      className="h-7 bg-surface-active border-border-strong text-foreground text-xs rounded-sm mb-1"
                    />
                    <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
                      {/* Voltar para fila */}
                      {!transferSearch && (
                        <>
                          <DropdownMenuItem
                            className="text-foreground-secondary focus:bg-surface-hover py-1.5 px-2 cursor-pointer text-xs"
                            onClick={handleReturnToQueue}
                          >
                            <div className="flex items-center gap-2">
                              <RotateCcw className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400" />
                              <span>Voltar para fila</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-surface-active my-1" />
                        </>
                      )}

                      {/* Departamentos */}
                      {filteredDepartments.length > 0 && (
                        <>
                          <div className="px-2 py-1">
                            <span className="text-[10px] text-foreground-muted font-semibold uppercase tracking-wider">
                              Departamentos
                            </span>
                          </div>
                          {filteredDepartments.map((dept) => (
                            <DropdownMenuItem
                              key={dept.id}
                              className="text-foreground focus:bg-surface-hover py-1.5 px-2 cursor-pointer text-xs"
                              onClick={() =>
                                handleTransferToDepartment(dept.id)
                              }
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Building2 className="h-3.5 w-3.5 text-blue-700 dark:text-blue-400 shrink-0" />
                                <span className="truncate">{dept.name}</span>
                                {dept.members && (
                                  <span className="text-[10px] text-foreground-muted shrink-0 ml-auto">
                                    {dept.members.length}{" "}
                                    {dept.members.length === 1
                                      ? "membro"
                                      : "membros"}
                                  </span>
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="bg-surface-active my-1" />
                        </>
                      )}

                      {/* Agentes */}
                      {filteredTransferUsers.length > 0 && (
                        <div className="px-2 py-1">
                          <span className="text-[10px] text-foreground-muted font-semibold uppercase tracking-wider">
                            Agentes
                          </span>
                        </div>
                      )}
                      {filteredTransferUsers.length === 0 &&
                      filteredDepartments.length === 0 ? (
                        <div className="text-foreground-muted text-[11px] px-2 py-2 text-center">
                          {transferSearch ? "Não encontrado" : "Sem agentes"}
                        </div>
                      ) : (
                        filteredTransferUsers.map((u) => (
                          <DropdownMenuItem
                            key={u.id}
                            className="text-foreground focus:bg-surface-hover py-1.5 px-2 cursor-pointer text-xs"
                            onClick={() => handleTransfer(u.id)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="relative shrink-0">
                                <User className="h-3.5 w-3.5" />
                                <Circle
                                  className={`h-1.5 w-1.5 absolute -bottom-0.5 -right-0.5 ${
                                    u.isOnline && u.presenceStatus !== "away"
                                      ? "fill-emerald-500 text-emerald-500"
                                      : "fill-foreground-muted text-foreground-muted"
                                  }`}
                                />
                              </div>
                              <span className="truncate">{u.fullName}</span>
                              {u.presenceStatus === "away" && (
                                <span className="text-[10px] text-foreground-muted shrink-0">
                                  Ausente
                                </span>
                              )}
                              {!u.isOnline && (
                                <span className="text-[10px] text-foreground-muted shrink-0">
                                  Offline
                                </span>
                              )}
                            </div>
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="flex-1" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={endCall}
                      className="h-11 w-11 rounded-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      <PhoneOff className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-surface-active text-foreground border-border-strong"
                  >
                    Encerrar chamada
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
